import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertTriangleIcon,
  CalendarIcon,
  DollarSignIcon,
  PackageIcon,
  TrendingDownIcon,
} from "lucide-react";

type InventoryReportsSummaryCardsProps = {
  totalExpiringLots: number;
  totalExpiringValueLak: number;
  totalWastageQty: number;
  totalWastageCostLak: number;
  avgWastagePerDay: number;
  isLoading?: boolean;
};

export function InventoryReportsSummaryCards({
  totalExpiringLots,
  totalExpiringValueLak,
  totalWastageQty,
  totalWastageCostLak,
  avgWastagePerDay,
  isLoading,
}: InventoryReportsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="">
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {/* TOTAL EXPIRING LOTS */}
      <Card className="rounded-xl border border-yellow-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-yellow-500/10">
            <AlertTriangleIcon className="size-5 text-yellow-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">Lot ໃກ້ຫມົດອາຍຸ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalExpiringLots} />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL EXPIRING VALUE */}
      <Card className="rounded-xl border border-orange-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
            <DollarSignIcon className="size-5 text-orange-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ມູນຄ່າ Lot ໃກ້ຫມົດອາຍຸ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalExpiringValueLak} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL WASTAGE QTY */}
      <Card className="rounded-xl border border-red-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red-500/10">
            <PackageIcon className="size-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຈຳນວນຂອງເສຍ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber
                value={totalWastageQty}
                formatOptions={{ maximumFractionDigits: 2 }}
              />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL WASTAGE COST */}
      <Card className="rounded-xl border border-purple-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
            <TrendingDownIcon className="size-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຄ່າໃຊ້ຈ່າຍຂອງເສຍ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalWastageCostLak} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* AVG WASTAGE PER DAY */}
      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <CalendarIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຂອງເສຍສະເລ່ຍຕໍ່ວັນ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber
                value={avgWastagePerDay}
                formatOptions={{ maximumFractionDigits: 2 }}
              />
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
